// src/Api.js
// Prod (driver.sitaxipro.com): REACT_APP_API_BASE="" → same-origin relative paths
// Dev: unset → localhost backend
const _apiEnv = process.env.REACT_APP_API_BASE;
const API_BASE =
  _apiEnv === undefined || _apiEnv === null
    ? "http://127.0.0.1:5000"
    : String(_apiEnv).replace(/\/$/, "");

/* =========================
   Storage helpers
========================= */
function getToken() {
  return localStorage.getItem("token") || "";
}

function getParkKey() {
  return localStorage.getItem("park_key") || "";
}

function getAdminToken() {
  return localStorage.getItem("admin_token") || "";
}

/* =========================
   Error normalize
========================= */
function toText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function normalizeDetail(detail) {
  // FastAPI/Pydantic 422: {detail:[{loc,msg,type},...]}
  if (Array.isArray(detail)) {
    const parts = detail
      .map((x) => {
        if (!x) return "";
        if (typeof x === "string") return x;
        const loc = Array.isArray(x.loc) ? x.loc.join(".") : "";
        const msg = x.msg || x.message || "";
        if (loc && msg) return `${loc}: ${msg}`;
        return msg || toText(x);
      })
      .filter(Boolean);

    return parts.join(" | ") || "Geçersiz istek (422).";
  }

  if (typeof detail === "object") return toText(detail);
  return toText(detail) || "Hata";
}

function apiErrorFromResponse(res, data) {
  const detail = data?.detail;
  const message = data?.message || data?.error;

  let msg = "";
  if (detail !== undefined) msg = normalizeDetail(detail);
  else if (message) msg = toText(message);
  else msg = `${res.status} ${res.statusText}` || "Hata";

  const err = new Error(msg);
  err.status = res.status;
  err.data = data;
  return err;
}

/* =========================
   Core request
========================= */
/**
 * request(path, opts)
 * - auth: true => Bearer token (driver)
 * - adminAuth: true => Bearer admin_token (admin)
 * - body: object => JSON
 */
async function request(
  path,
  { method = "GET", body, auth = false, adminAuth = false, signal } = {}
) {
  const headers = {
    Accept: "application/json",
  };

  // ✅ Admin endpoint’lerinde X-Park-Key göndermiyoruz
  if (!adminAuth) {
    const parkKey = getParkKey();
    if (parkKey) headers["X-Park-Key"] = parkKey;
  }

  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (adminAuth) {
    const adminToken = getAdminToken();
    if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;
  } else if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

/* =========================
   PARKS
========================= */
export async function fetchParks() {
  return request("/parks", { method: "GET" }); // {parks:[{key,label}]}
}

/* =========================
   AUTH (Driver)
========================= */
export async function loginWithPhone(phone, parkKey) {
  return request("/auth/login", {
    method: "POST",
    body: { phone, park_key: parkKey },
  });
}

/** OTP SMS iste — kod sadece backend'de uretilir */
export async function requestOtp(phone, parkKey) {
  return request("/auth/otp/request", {
    method: "POST",
    body: { phone, park_key: parkKey },
  });
}

/** OTP dogrula + JWT */
export async function verifyOtp(phone, code, parkKey) {
  return request("/auth/verify", {
    method: "POST",
    body: { phone, code, park_key: parkKey },
  });
}

export async function fetchMe(token) {
  const r = await fetch(`${API_BASE}/me`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

/* =========================
   RANKINGS (Driver)
========================= */
export async function fetchRankings() {
  return request("/rankings", { method: "GET", auth: true });
}

export async function startRankingsRefresh() {
  return request("/rankings/refresh", { method: "POST", auth: true });
}

export async function getRankingsRefreshStatus(jobId) {
  const qs = `?job_id=${encodeURIComponent(jobId)}`;
  return request(`/rankings/refresh/status${qs}`, { method: "GET", auth: true });
}

export async function refreshRankings() {
  return startRankingsRefresh();
}

/* =========================
   BONUSES
========================= */
export async function fetchBonuses(token) {
  const r = await fetch(`${API_BASE}/bonuses`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

export async function fetchBonusesWithStoredToken() {
  return fetchBonuses(getToken());
}

/* =========================
   CAR
========================= */
export async function fetchMyCar() {
  return request("/car/me", { method: "GET", auth: true });
}

export async function changeMyCar(payload) {
  const body =
    typeof payload === "string"
      ? { plate: payload }
      : payload && typeof payload === "object"
      ? payload
      : {};

  return request("/car/change", { method: "POST", auth: true, body });
}

export async function updateMyCar(payload) {
  return request("/car/update", { method: "PUT", auth: true, body: payload || {} });
}

export async function selectMyCar(carId) {
  return request("/car/select", {
    method: "POST",
    auth: true,
    body: { car_id: String(carId || "").trim() },
  });
}

export async function fetchProfileEdit() {
  return request("/profile/edit", { method: "GET", auth: true });
}

export async function updateMyProfile(payload) {
  return request("/profile/update", { method: "PUT", auth: true, body: payload || {} });
}

export async function fetchBrands() {
  return request("/car/brands", { method: "GET", auth: true });
}

export async function fetchModels(brand) {
  return request("/car/models", { method: "POST", auth: true, body: { brand } });
}

export async function fetchColors() {
  return request("/car/colors", { method: "GET", auth: true });
}

/* =========================
   CONTACTS
========================= */
export async function fetchContacts() {
  const token = getToken();
  const r = await fetch(`${API_BASE}/contacts`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });

  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

export async function fetchContactsAll() {
  return request("/contacts/all", { method: "GET", auth: true });
}

/* =========================
   REFERRALS (Driver/Admin)
========================= */
export async function createReferral(payload) {
  return request("/referrals", { method: "POST", auth: true, body: payload });
}

export async function fetchMyReferrals() {
  return request("/referrals/me", { method: "GET", auth: true });
}

export async function adminListReferrals(opts = {}) {
  const refresh = opts?.refresh ? "1" : "0";
  return request(`/admin/referrals?refresh=${refresh}`, { method: "GET", adminAuth: true });
}

export async function adminUpdateReferral(refId, payload) {
  return request(`/admin/referrals/${encodeURIComponent(refId)}`, {
    method: "PUT",
    adminAuth: true,
    body: payload,
  });
}

/* =========================
   WITHDRAWALS (Driver/Admin)
========================= */
export async function createWithdrawal(payload) {
  return request("/withdrawals", { method: "POST", auth: true, body: payload });
}

export async function fetchMySipayWallet() {
  return request("/withdrawals/sipay-wallet", { method: "GET", auth: true });
}

export async function fetchMyWithdrawals() {
  return request("/withdrawals/me", { method: "GET", auth: true });
}

export async function fetchAutoWithdrawSchedule() {
  return request("/withdrawals/auto-schedule", { method: "GET", auth: true });
}

export async function upsertAutoWithdrawSchedule(payload) {
  return request("/withdrawals/auto-schedule", { method: "PUT", auth: true, body: payload });
}

export async function deleteAutoWithdrawSchedule() {
  return request("/withdrawals/auto-schedule", { method: "DELETE", auth: true });
}

export async function adminListWithdrawals(filters = {}) {
  const params = new URLSearchParams();
  const f = filters || {};
  if (f.date_from) params.set("date_from", f.date_from);
  if (f.date_to) params.set("date_to", f.date_to);
  if (f.park_key) params.set("park_key", f.park_key);
  if (f.phone) params.set("phone", f.phone);
  if (f.name) params.set("name", f.name);
  if (f.status) params.set("status", f.status);
  if (f.payout_method) params.set("payout_method", f.payout_method);
  if (f.include_pending != null) params.set("include_pending", String(f.include_pending));
  const q = params.toString() ? `?${params.toString()}` : "";
  return request(`/admin/withdrawals${q}`, { method: "GET", adminAuth: true });
}

export async function adminDownloadWithdrawalsExcel(filters = {}) {
  const headers = { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;

  const params = new URLSearchParams();
  const f = filters || {};
  if (f.park_key) params.set("park_key", f.park_key);
  if (f.date_from) params.set("date_from", f.date_from);
  if (f.date_to) params.set("date_to", f.date_to);
  if (f.phone) params.set("phone", f.phone);
  if (f.name) params.set("name", f.name);
  if (f.status) params.set("status", f.status);
  if (f.payout_method) params.set("payout_method", f.payout_method);
  const q = params.toString() ? `?${params.toString()}` : "";

  const r = await fetch(`${API_BASE}/admin/withdrawals/export.xlsx${q}`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  let filename = "odeme_gecmisi.xlsx";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, filename };
}

export async function adminListPaymentMandates() {
  return request("/admin/payment-mandates", { method: "GET", adminAuth: true });
}

export async function adminDownloadMandatesExcel() {
  const headers = { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const r = await fetch(`${API_BASE}/admin/payment-mandates/export.xlsx`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  let filename = "odeme_talimatlari.xlsx";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, filename };
}

export async function adminPaymentMandateHistory(date) {
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  return request(`/admin/payment-mandate-history${q}`, { method: "GET", adminAuth: true });
}

export async function adminDownloadMandateHistoryExcel(date, category) {
  const headers = { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (category) params.set("category", category);
  const q = params.toString() ? `?${params.toString()}` : "";
  const r = await fetch(`${API_BASE}/admin/payment-mandate-history/export.xlsx${q}`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  let filename = "talimat_history.xlsx";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, filename };
}

export async function adminUploadPaymentMandates(file) {
  const headers = { Accept: "application/json" };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API_BASE}/admin/payment-mandates/upload`, {
    method: "POST",
    headers,
    body: fd,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

export async function adminPaymentMandateUploadProgress(batchId) {
  return request(`/admin/payment-mandates/upload-progress/${encodeURIComponent(batchId)}`, {
    method: "GET",
    adminAuth: true,
  });
}

export async function adminPaymentMandateUploadControl(batchId, action) {
  return request(`/admin/payment-mandates/upload-control/${encodeURIComponent(batchId)}`, {
    method: "POST",
    adminAuth: true,
    body: { action },
  });
}

export async function adminDownloadMandateFailedExcel(batchId) {
  const headers = {
    Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const q = batchId ? `?batch_id=${encodeURIComponent(batchId)}` : "";
  const r = await fetch(`${API_BASE}/admin/payment-mandates/upload-failed.xlsx${q}`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  let filename = "Toplu-Iban-Transferi-Sonuc.xlsx";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m && m[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, filename };
}

export async function adminClearPaymentMandates() {
  return request("/admin/payment-mandates", {
    method: "DELETE",
    adminAuth: true,
  });
}

export async function adminDownloadMandateTemplate() {
  const headers = {
    Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const r = await fetch(`${API_BASE}/admin/payment-mandates/template.xlsx`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "odeme_talimati_sablon.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}

export async function adminUpdatePaymentMandate(id, payload) {
  return request(`/admin/payment-mandates/${encodeURIComponent(id)}`, {
    method: "PUT",
    adminAuth: true,
    body: payload,
  });
}

export async function adminDeletePaymentMandate(id) {
  return request(`/admin/payment-mandates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    adminAuth: true,
  });
}

export async function adminLookupMandateDriver(phone) {
  const q = phone ? `?phone=${encodeURIComponent(phone)}` : "";
  return request(`/admin/payment-mandates/driver-lookup${q}`, { method: "GET", adminAuth: true });
}

export async function adminAddPaymentMandate(payload) {
  return request("/admin/payment-mandates/add", {
    method: "POST",
    adminAuth: true,
    body: payload,
  });
}

export async function adminUpdateWithdrawal(withdrawalId, payload) {
  return request(`/admin/withdrawals/${encodeURIComponent(withdrawalId)}`, {
    method: "PUT",
    adminAuth: true,
    body: payload,
  });
}

export async function adminGetPayoutMaintenance() {
  return request("/admin/payout-maintenance", { method: "GET", adminAuth: true });
}

export async function adminGetPayoutBalances() {
  return request("/admin/payout-balances", { method: "GET", adminAuth: true });
}

export async function adminSetPayoutMaintenance(maintenance) {
  return request("/admin/payout-maintenance", { method: "POST", adminAuth: true, body: { maintenance } });
}

export async function adminSetVelixMaintenance(maintenance) {
  return request("/admin/payout-maintenance/velix", { method: "POST", adminAuth: true, body: { maintenance } });
}

export async function adminBulkSendWithdrawals(payload) {
  return request("/admin/withdrawals/bulk-send", {
    method: "POST",
    adminAuth: true,
    body: payload,
  });
}

export async function adminGetRewards() {
  return request("/admin/rewards", { method: "GET", adminAuth: true });
}

export async function adminSetRewards(payload) {
  return request("/admin/rewards", { method: "PUT", adminAuth: true, body: payload });
}

/* =========================
   ANNOUNCEMENTS (Driver)
========================= */
export async function fetchAnnouncements() {
  return request("/announcements", { method: "GET", auth: true });
}

/* =========================
   ADMIN AUTH + ANNOUNCEMENTS
========================= */
export async function adminLogin(password) {
  return request("/admin/login", { method: "POST", body: { password } });
}

export async function adminFetchParks() {
  return request("/admin/parks", { method: "GET", adminAuth: true });
}

export async function adminListAnnouncements() {
  return request("/admin/announcements", { method: "GET", adminAuth: true });
}

export async function adminCreateAnnouncement(payload) {
  return request("/admin/announcements", {
    method: "POST",
    adminAuth: true,
    body: payload,
  });
}

export async function adminUpdateAnnouncement(id, payload) {
  return request(`/admin/announcements/${encodeURIComponent(id)}`, {
    method: "PUT",
    adminAuth: true,
    body: payload,
  });
}

export async function adminDeleteAnnouncement(id) {
  return request(`/admin/announcements/${encodeURIComponent(id)}`, {
    method: "DELETE",
    adminAuth: true,
  });
}

/* =========================
   ADMIN • PARKS / CAMPAIGN
========================= */
export async function adminGetParks() {
  return request("/admin/parks", { method: "GET", adminAuth: true });
}

export async function adminGetCampaign(park_key) {
  const qs = `?park_key=${encodeURIComponent(park_key)}`;
  return request(`/admin/campaign${qs}`, { method: "GET", adminAuth: true });
}

export async function adminSetCampaign(payload) {
  return request("/admin/campaign", {
    method: "PUT",
    adminAuth: true,
    body: payload,
  });
}

export async function adminClearCampaign(park_key) {
  const qs = `?park_key=${encodeURIComponent(park_key)}`;
  return request(`/admin/campaign/clear${qs}`, {
    method: "POST",
    adminAuth: true,
  });
}

/* =========================
   ADMIN • RANKINGS
   Backend:
   - GET  /admin/rankings/current?park_key=...
   - POST /admin/rankings/refresh?park_key=...
   - GET  /admin/rankings/refresh/status?job_id=...
   - GET  /admin/rankings/day-top20?park_key=...&date=YYYY-MM-DD
========================= */
export async function adminGetRankingsBoard(park_key) {
  if (!park_key) throw new Error("park_key zorunlu");
  const qs = `?park_key=${encodeURIComponent(park_key)}`;
  return request(`/admin/rankings/current${qs}`, {
    method: "GET",
    adminAuth: true,
  });
}

// (İstersen bunu tamamen kaldırabiliriz; board zaten current endpoint’ine gidiyor.)
// Geriye uyum için bırakıyorum:
export async function adminGetRankingsCurrent(park_key) {
  return adminGetRankingsBoard(park_key);
}

export async function adminStartRankingsRefresh(park_key) {
  if (!park_key) throw new Error("park_key zorunlu");
  const qs = `?park_key=${encodeURIComponent(park_key)}`;
  return request(`/admin/rankings/refresh${qs}`, {
    method: "POST",
    adminAuth: true,
  });
}

export async function adminGetRefreshStatus(jobId) {
  if (!jobId) throw new Error("job_id zorunlu");
  const qs = `?job_id=${encodeURIComponent(jobId)}`;
  return request(`/admin/rankings/refresh/status${qs}`, {
    method: "GET",
    adminAuth: true,
  });
}

export async function adminDayTop20(park_key, date) {
  if (!park_key) throw new Error("park_key zorunlu");
  if (!date) throw new Error("date zorunlu");

  const qs =
    `?park_key=${encodeURIComponent(park_key)}` +
    `&date=${encodeURIComponent(date)}`;

  return request(`/admin/rankings/day-top20${qs}`, {
    method: "GET",
    adminAuth: true,
  });
}

/* =========================
   ADMIN • VakıfBank TÖS
========================= */
export async function adminVakifbankConfig() {
  return request("/admin/vakifbank/config", { method: "GET", adminAuth: true });
}

export async function adminVakifbankConnectionTest() {
  return request("/admin/vakifbank/connection-test", { method: "GET", adminAuth: true });
}

export async function adminVakifbankPurposeCodes() {
  return request("/admin/vakifbank/payment-purpose-codes", { method: "GET", adminAuth: true });
}

export async function adminVakifbankRecipients() {
  return request("/admin/vakifbank/recipients", { method: "GET", adminAuth: true });
}

export async function adminVakifbankListBatches() {
  return request("/admin/vakifbank/batches", { method: "GET", adminAuth: true });
}

export async function adminVakifbankGetBatch(batchId) {
  return request(`/admin/vakifbank/batches/${encodeURIComponent(batchId)}`, {
    method: "GET",
    adminAuth: true,
  });
}

export async function adminVakifbankCreateBatch(payload) {
  return request("/admin/vakifbank/batches", {
    method: "POST",
    adminAuth: true,
    body: payload,
  });
}

export async function adminVakifbankCancelBatch(batchId) {
  return request(`/admin/vakifbank/batches/${encodeURIComponent(batchId)}/cancel`, {
    method: "POST",
    adminAuth: true,
  });
}

export async function adminVakifbankSendNow(batchId) {
  return request(`/admin/vakifbank/batches/${encodeURIComponent(batchId)}/send-now`, {
    method: "POST",
    adminAuth: true,
  });
}

export async function adminVakifbankSmsApprove(batchId, smsCode) {
  return request(`/admin/vakifbank/batches/${encodeURIComponent(batchId)}/sms-approve`, {
    method: "POST",
    adminAuth: true,
    body: { sms_code: smsCode },
  });
}

export async function adminVakifbankSmsSend(batchId) {
  return request(`/admin/vakifbank/batches/${encodeURIComponent(batchId)}/sms-send`, {
    method: "POST",
    adminAuth: true,
  });
}

export async function adminVakifbankQueryItem(itemId) {
  return request(`/admin/vakifbank/items/${encodeURIComponent(itemId)}/query`, {
    method: "POST",
    adminAuth: true,
  });
}

export async function adminVakifbankParseExcel(file) {
  const headers = { Accept: "application/json" };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API_BASE}/admin/vakifbank/parse-excel`, {
    method: "POST",
    headers,
    body: fd,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFromResponse(r, data);
  return data;
}

export async function adminVakifbankDownloadTemplate() {
  const headers = {
    Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const adminToken = getAdminToken();
  if (adminToken) headers.Authorization = `Bearer ${adminToken}`;
  const r = await fetch(`${API_BASE}/admin/vakifbank/template.xlsx`, {
    method: "GET",
    headers,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw apiErrorFromResponse(r, data);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "vakifbank_odeme_sablon.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}

export async function adminVakifbankTyConfig() {
  return request("/admin/vakifbank-ty/config", { method: "GET", adminAuth: true });
}

export async function adminVakifbankTyHistory(limit = 50) {
  return request(`/admin/vakifbank-ty/history?limit=${encodeURIComponent(limit)}`, {
    method: "GET",
    adminAuth: true,
  });
}

export async function adminVakifbankTySend(payload) {
  return request("/admin/vakifbank-ty/send", {
    method: "POST",
    adminAuth: true,
    body: payload,
  });
}

export async function adminVakifbankTyQuery(kurumIslemId) {
  return request(`/admin/vakifbank-ty/query/${encodeURIComponent(kurumIslemId)}`, {
    method: "GET",
    adminAuth: true,
  });
}

export async function adminVakifbankEkstreConfig() {
  return request("/admin/vakifbank/ekstre/config", { method: "GET", adminAuth: true });
}

export async function adminVakifbankEkstreMovements(fromDate, toDate) {
  const q = new URLSearchParams();
  if (fromDate) q.set("from_date", fromDate);
  if (toDate) q.set("to_date", toDate);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return request(`/admin/vakifbank/ekstre/movements${suffix}`, { method: "GET", adminAuth: true });
}
